import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Calendar, AlertCircle, GraduationCap, ArrowLeft } from 'lucide-react';

export default function ParentLoginPage() {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(searchParams.get('expired') ? 'Your session has expired. Please sign in again.' : '');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!studentId) {
      setError('Please enter Birth Registration Number');
      return;
    }

    setLoading(true);

    try {
      const API_URL = '';
      const payload = { student_id: studentId.trim() };
      if (dateOfBirth) payload.date_of_birth = dateOfBirth;
      
      const response = await fetch(`${API_URL}/api/parent/login-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMsg = 'Login failed';
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          try { const err = await response.json(); errorMsg = err.detail || errorMsg; } catch(e) {}
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      
      // Store token and student info
      localStorage.setItem('parentToken', data.access_token);
      localStorage.setItem('studentData', JSON.stringify(data.student));
      
      // Navigate to parent dashboard
      navigate('/parent/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid Birth Registration Number or Date of Birth. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4 gap-2 text-gray-600 hover:text-gray-900"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        {/* School Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg opacity-20 blur"></div>
              <div className="relative bg-white p-4 rounded-lg shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_edtech-penama/artifacts/4xob6y79_logo12_9_73521.jpeg" 
                  alt="Penama Education Portal" 
                  className="h-24 w-24 object-contain"
                />
              </div>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-1">PENAMA</h1>
          <h2 className="text-2xl font-semibold text-blue-600 mb-2">Education Portal</h2>
          <p className="text-gray-600 font-medium">Parent Portal</p>
          <p className="text-xs text-gray-500 mt-2 px-3 py-1 bg-blue-50 rounded-full inline-block">View Your Child's Report Card</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader className="space-y-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
              <GraduationCap className="h-6 w-6" />
              Welcome Parents
            </CardTitle>
            <CardDescription className="text-center text-blue-100">
              Enter your child's details to view their report card
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Birth Registration Number Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" />
                  Birth Registration Number
                </label>
                <Input
                  type="text"
                  placeholder="e.g., 1037689"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  className="text-lg h-12"
                  autoComplete="off"
                  disabled={loading}
                  data-testid="student-id-input"
                />
                <p className="text-xs text-gray-500">Enter your child's birth registration (passport) number</p>
              </div>

              {/* Date of Birth Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  Date of Birth <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="text-lg h-12"
                  disabled={loading}
                  min="1990-01-01"
                  max="2022-12-31"
                  data-testid="dob-input"
                />
                <p className="text-xs text-gray-500">Enter your child's date of birth</p>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                disabled={loading}
                data-testid="parent-login-btn"
              >
                {loading ? 'Logging in...' : 'View Report Card'}
              </Button>

              {/* Help Text */}
              <div className="text-center pt-4 space-y-2 bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800 font-medium">
                  Need Help?
                </p>
                <p className="text-xs text-amber-700">
                  The Birth Registration Number can be found on your child's birth certificate or passport. Contact the school office if you need assistance.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-600">
          <p>Academic Year 2025-2026</p>
          <p className="text-xs mt-2">Penama Education Portal - Vanuatu</p>
        </div>
      </div>
    </div>
  );
}
